/** Guided coach-mark tour — the answer to "I don't know how to proceed".
 *
 * Spotlights one element at a time (a nav item, a card) and explains what it
 * is and what to do with it. Deliberately dumb: it takes already-translated
 * steps, so the copy lives with the caller and this file stays testable.
 *
 * A step whose target is missing (or that has no target at all, like the
 * welcome/closing cards) degrades to a centred dialog rather than pointing at
 * nothing — so the tour never breaks because a role can't see some menu item.
 */
import { useCallback, useEffect, useState } from "react";
import { useT } from "../../i18n";

export interface TourStep {
  /** CSS selector for the element to spotlight. Omit for a centred card. */
  target?: string;
  title: string;
  body: string;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PAD = 6;

export function ProductTour({
  steps,
  onClose,
}: {
  steps: TourStep[];
  onClose: () => void;
}) {
  const t = useT();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  const step = steps[index];

  // Measure the current target (and re-measure on resize/scroll) so the
  // spotlight tracks the element even if the layout shifts.
  useEffect(() => {
    if (!step) return;
    const measure = () => {
      const el = step.target
        ? (document.querySelector(step.target) as HTMLElement | null)
        : null;
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step]);

  const finish = useCallback(() => onClose(), [onClose]);

  // Esc leaves the tour — never trap someone inside it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [finish]);

  if (!step) return null;

  const isLast = index === steps.length - 1;
  const next = () => (isLast ? finish() : setIndex((i) => i + 1));
  const back = () => setIndex((i) => Math.max(0, i - 1));

  // Card sits under the spotlight when there is one, centred otherwise.
  const cardStyle = rect
    ? {
        position: "fixed" as const,
        top: rect.top + rect.height + 14,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 340)),
        width: 320,
      }
    : {
        position: "fixed" as const,
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 340,
      };

  return (
    <div className="z-50">
      {rect ? (
        // The huge spread shadow dims everything EXCEPT the cut-out.
        <div
          data-testid="tour-spotlight"
          aria-hidden
          style={{
            position: "fixed",
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 12,
            boxShadow: "0 0 0 9999px rgba(36, 38, 26, 0.62)",
            pointerEvents: "none",
            zIndex: 50,
          }}
        />
      ) : (
        <div
          aria-hidden
          className="fixed inset-0 z-50"
          style={{ background: "rgba(36, 38, 26, 0.62)" }}
        />
      )}

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        style={{ ...cardStyle, zIndex: 51 }}
        className="rounded-card border border-line bg-surface p-4 shadow-xl"
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">
          {t("tour.step", { n: index + 1, total: steps.length })}
        </p>
        <h2 id="tour-title" className="mt-1 text-base font-extrabold text-ink">
          {step.title}
        </h2>
        <p className="mt-1 text-sm text-muted">{step.body}</p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={finish}
            className="text-sm font-semibold text-muted hover:text-ink"
          >
            {t("tour.skip")}
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button
                type="button"
                onClick={back}
                className="rounded-control border border-line px-3 py-1.5 text-sm font-semibold text-ink hover:border-brand"
              >
                {t("tour.back")}
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="rounded-control bg-brand px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark"
            >
              {isLast ? t("tour.done") : t("tour.next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
